import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Menu, X, Briefcase, BookOpen } from 'lucide-react';
import { Button } from './ui/button';

export const Navbar = () => {
  const navigate = useNavigate();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Home', href: '#home' },
    { name: 'Services', href: '#services' },
    { name: 'Why Us', href: '#why-us' },
    { name: 'Join Fleet', href: '#join-fleet' },
    { name: 'Contact', href: '#contact' }
  ];

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMobileMenuOpen(false);
    }
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-white/95 backdrop-blur-md shadow-md'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <div className="flex items-center space-x-3">
            <img
              src="https://customer-assets.emergentagent.com/job_freight-flow-51/artifacts/jlvfmutc_logo%20files.png"
              alt="Primeswift Solutions"
              className="h-12 w-auto"
            />
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <button
                key={link.name}
                onClick={() => scrollToSection(link.href)}
                className={`text-sm font-medium transition-colors hover:text-blue-600 px-1 ${
                  isScrolled ? 'text-gray-700' : 'text-white'
                }`}
              >
                {link.name}
              </button>
            ))}
            <button
              data-testid="nav-driver-jobs-link"
              onClick={() => navigate('/driver-jobs')}
              className={`text-sm font-medium transition-colors hover:text-blue-600 px-1 flex items-center gap-1.5 ${
                isScrolled ? 'text-gray-700' : 'text-white'
              }`}
            >
              <Briefcase size={15} />
              Career
            </button>
            <button
              data-testid="nav-blog-link"
              onClick={() => navigate('/blog')}
              className={`text-sm font-medium transition-colors hover:text-blue-600 px-1 flex items-center gap-1.5 ${
                isScrolled ? 'text-gray-700' : 'text-white'
              }`}
            >
              <BookOpen size={15} />
              Blog
            </button>
            <Button
              onClick={() => scrollToSection('#get-quote')}
              className="bg-blue-600 hover:bg-blue-700 text-white ml-2"
            >
              Get Quote
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className={`md:hidden p-2 rounded-lg transition-colors ${
              isScrolled ? 'text-gray-700' : 'text-white'
            }`}
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-white border-t shadow-lg">
          <div className="px-4 py-4 space-y-3">
            {navLinks.map((link) => (
              <button
                key={link.name}
                onClick={() => scrollToSection(link.href)}
                className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-blue-50 hover:text-blue-600 rounded-lg transition-colors"
              >
                {link.name}
              </button>
            ))}
            <button
              data-testid="mobile-nav-driver-jobs-link"
              onClick={() => { navigate('/driver-jobs'); setIsMobileMenuOpen(false); }}
              className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-blue-50 hover:text-blue-600 rounded-lg transition-colors flex items-center gap-2"
            >
              <Briefcase size={16} />
              Career
            </button>
            <button
              data-testid="mobile-nav-blog-link"
              onClick={() => { navigate('/blog'); setIsMobileMenuOpen(false); }}
              className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-blue-50 hover:text-blue-600 rounded-lg transition-colors flex items-center gap-2"
            >
              <BookOpen size={16} />
              Blog
            </button>
            <Button
              onClick={() => scrollToSection('#get-quote')}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white"
            >
              Get Quote
            </Button>
          </div>
        </div>
      )}
    </nav>
  );
};
